import * as React from 'react';
import { Loader2, ChevronDown, ChevronUp, FileDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useOptimisationRun } from '@/hooks/optimisation/use-optimisation-run';
import { useOptimisationCompare } from '@/hooks/optimisation/use-optimisation-compare';
import { useCreateExport } from '@/hooks/exports/use-create-export';
import { triggerDownload } from '@/lib/download';
import { useToast } from '@/hooks/use-toast';
import { RunStatusBadge } from '@/pages/projects/components/run-status-badge';
import { BarLayoutChart } from './bar-layout-chart';
import { CutTable } from './cut-table';
import { RunComparison } from './run-comparison';
import type { OptimisationBarLayoutDto } from '@cutlyft/zod-schemas';

interface RunDetailProps {
  runId: number;
  compareWithId?: number;
}

function mmStr(mm: number | null) {
  if (mm == null) return '—';
  return mm >= 1000 ? `${(mm / 1000).toFixed(2)} m` : `${mm} mm`;
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="text-sm">
      <p className="text-fg-muted text-xs">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}

function BarLayoutRow({ bl }: { bl: OptimisationBarLayoutDto }) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium hover:bg-accent/40 transition-colors"
        onClick={() => setExpanded((x) => !x)}
      >
        <span>Bar {bl.barIndex + 1} — {bl.cuts.length} cuts · waste {mmStr(bl.wasteMm)}</span>
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {expanded && (
        <div className="border-t">
          <CutTable barLayout={bl} />
        </div>
      )}
    </div>
  );
}

export function RunDetail({ runId, compareWithId }: RunDetailProps) {
  const { toast } = useToast();
  const { data: run, isLoading } = useOptimisationRun(runId);
  const { data: comparison } = useOptimisationCompare(
    compareWithId ? runId : undefined,
    compareWithId,
  );
  const createExport = useCreateExport();

  const handleDownloadLayoutPdf = () => {
    createExport.mutate(
      { exportType: 'LAYOUT_PDF', runId },
      {
        onSuccess: (result) => {
          triggerDownload(result.downloadUrl);
        },
        onError: () => {
          toast({ variant: 'destructive', title: 'Failed to generate Layout PDF' });
        },
      },
    );
  };

  if (isLoading || !run) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base">Run #{run.id}</CardTitle>
              <RunStatusBadge status={run.runStatus} />
            </div>
            {run.runStatus === 'COMPLETED' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadLayoutPdf}
                disabled={createExport.isPending}
              >
                {createExport.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <FileDown className="h-3.5 w-3.5 mr-1.5" />
                )}
                Layout PDF
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {run.runStatus === 'COMPLETED' && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Bars used" value={run.totalBarsUsed ?? '—'} />
              <Stat label="Total waste" value={mmStr(run.totalWasteMm)} />
              <Stat label="Offcuts produced" value={mmStr(run.totalOffcutsProducedMm)} />
              <Stat label="Algorithm" value={run.algorithmUsed ?? '—'} />
            </div>
          )}
          {run.runStatus === 'FAILED' && (
            <p className="text-sm text-destructive">{run.errorText ?? 'Run failed.'}</p>
          )}
          {(run.runStatus === 'QUEUED' || run.runStatus === 'RUNNING') && (
            <p className="text-sm text-fg-muted">Run in progress…</p>
          )}
        </CardContent>
      </Card>

      {/* Bar layout chart */}
      {run.barLayouts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Bar Layouts</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pr-2">
            <BarLayoutChart barLayouts={run.barLayouts} />
          </CardContent>
        </Card>
      )}

      {/* Per-bar cut breakdown */}
      {run.barLayouts.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Cut Breakdown</h3>
          {run.barLayouts.map((bl) => (
            <BarLayoutRow key={bl.id} bl={bl} />
          ))}
        </div>
      )}

      {/* Comparison */}
      {comparison && <RunComparison comparison={comparison} />}
    </div>
  );
}
